import React from 'react';
import { styled } from 'linaria/react';
import { HtmlBannerRowItem } from './HtmlBannerRowItem';
import { theme } from '../../../Theme';
import useVisibilitySettings from '../../../../utils/useVisibilitySettings';
import SlickSlider from 'react-slick';
import { CustomArrow, Dot, Dots } from '../Slider/Slider';
import { useIntl } from '@jetshop/intl';
import { Above } from '@jetshop/ui/Breakpoints';

const HtmlBannerRowWrapper = styled('div')`
  display: grid;
  grid-template-columns: repeat(${props => props.rowcolumns}, 1fr);
  gap: ${props => props.gap};
  width: 100%;
  max-width: ${props => props.maxwidth};
  margin: auto;

  ${theme.below.lg} {
    flex-direction: column;
  }
`;

export const HtmlBannerRow = ({
  fullWidth,
  gap,
  sweden,
  denmark,
  norway,
  finland,
  netherlands,
  eu,
  desktopColumns,
  mobileColumns,
  asSlider,
  children
}) => {
  const visibilitySettings = {
    sweden: sweden?.value,
    denmark: denmark?.value,
    norway: norway?.value,
    finland: finland?.value,
    netherlands: netherlands?.value,
    eu: eu?.value
  };
  const doNotRender = useVisibilitySettings(visibilitySettings);
  const maxWidth = fullWidth?.value ? '100%' : '90rem';
  const elementGap = gap?.value;
  const addedPropsChildren = [];

  const translate = useIntl();
  const slideText = translate('Slide');

  const settings = {
    dots: true,
    infinite: true,
    lazyLoad: false,
    fade: true,
    speed: 500,
    autoplay: true,
    autoplaySpeed: 3000,
    slidesToShow: 1,
    slidesToScroll: 1,
    nextArrow: <CustomArrow />,
    prevArrow: <CustomArrow />,
    appendDots: dots => <Dots>{dots}</Dots>,
    customPaging: i => (
      <Dot aria-label={`${slideText} ${i + 1}`} type="button" tabIndex={0} />
    )
  };
  const settingsMobile = {
    dots: true,
    infinite: true,
    lazyLoad: false,
    fade: true,
    autoplay: true,
    autoplaySpeed: 3000,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: false,
    appendDots: dots => <Dots>{dots}</Dots>,
    customPaging: i => (
      <Dot aria-label={`${slideText} ${i + 1}`} type="button" tabIndex={0} />
    )
  };

  // If the component should not be visible, return null
  if (doNotRender) {
    return null;
  }

  // Adds onlyChild prop to each child
  for (const child of children) {
    addedPropsChildren.push(
      <HtmlBannerRowItem
        onlyChild={{ value: children?.length === 1 }}
        {...child.props}
      />
    );
  }

  if (asSlider?.value) {
    return (
      <Above breakpoint="lg">
        {matches => (
          <SlickSlider {...(matches ? settings : settingsMobile)}>
            {children?.map((child, index) => (
              <HtmlBannerRowItem
                key={index}
                onlyChild={{ value: true }}
                {...child.props}
              />
            ))}
          </SlickSlider>
        )}
      </Above>
    );
  }

  const desktopCols =
    desktopColumns?.value ?? (children?.length >= 3 ? 3 : children?.length);
  const mobileCols = mobileColumns?.value ?? 1;

  return (
    <Above breakpoint="lg">
      {matches => (
        <HtmlBannerRowWrapper
          className="html-banner-row"
          maxwidth={maxWidth}
          gap={elementGap}
          rowcolumns={matches ? desktopCols : mobileCols}
        >
          {addedPropsChildren}
        </HtmlBannerRowWrapper>
      )}
    </Above>
  );
};
